import React, { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { X } from "lucide-react";
import ImageFullscreenDialog from "./imageFullscreenDialog";

const toMediaUrl = (item) => {
  if (item == null || item === "") return null;
  if (typeof item === "string") return item;
  if (typeof item === "object" && item.url) return String(item.url);
  return String(item);
};

const normalizeImageList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(toMediaUrl).filter(Boolean);
  }
  const single = toMediaUrl(value);
  return single ? [single] : [];
};

const ImageThumbnailGrid = ({ title, images = [], onImageClick }) => {
  if (!images.length) {
    return (
      <Box mb={2}>
        <Typography fontSize={14} fontWeight={600} mb={1} color="#225B27">
          {title}
        </Typography>
        <Typography fontSize={12} color="text.secondary">
          No images available
        </Typography>
      </Box>
    );
  }

  return (
    <Box mb={2.5}>
      <Typography fontSize={14} fontWeight={600} mb={1.5} color="#225B27">
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        {images.map((url, idx) => (
          <Grid item xs={6} sm={4} md={3} key={`${url}-${idx}`}>
            <Box
              onClick={() => onImageClick(images, idx)}
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #e0e0e0",
                bgcolor: "#fafafa",
                cursor: "pointer",
                transition: "box-shadow 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(34, 91, 39, 0.2)",
                },
              }}
            >
              <Box
                component="img"
                src={url}
                alt={`${title} ${idx + 1}`}
                sx={{
                  width: "100%",
                  height: 120,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const SubtaskImagesDialog = ({ open, onClose, subtask }) => {
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenImages, setFullscreenImages] = useState([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  const beforeImages = useMemo(
    () => normalizeImageList(subtask?.beforeImage),
    [subtask?.beforeImage]
  );
  const afterImages = useMemo(
    () => normalizeImageList(subtask?.afterImage),
    [subtask?.afterImage]
  );
  const receiptImages = useMemo(
    () => normalizeImageList(subtask?.receipt),
    [subtask?.receipt]
  );

  const handleImageClick = (images, index) => {
    setFullscreenImages(images);
    setFullscreenIndex(index);
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
    setFullscreenImages([]);
    setFullscreenIndex(0);
  };

  const locationLabel =
    subtask?.locationName || subtask?.locationNumber || "Subtask";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "20px" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Subtask Images — {locationLabel}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 3 }}>
          <ImageThumbnailGrid
            title="Before Images"
            images={beforeImages}
            onImageClick={handleImageClick}
          />
          <ImageThumbnailGrid
            title="After Images"
            images={afterImages}
            onImageClick={handleImageClick}
          />
          <ImageThumbnailGrid
            title="Receipt"
            images={receiptImages}
            onImageClick={handleImageClick}
          />
        </DialogContent>
      </Dialog>

      <ImageFullscreenDialog
        open={fullscreenOpen}
        images={fullscreenImages}
        initialIndex={fullscreenIndex}
        onClose={handleCloseFullscreen}
      />
    </>
  );
};

export default SubtaskImagesDialog;
